/**
 * VideoTheater — the big player, for actually watching a rep.
 *
 * The detail panel is 340px wide on desktop, which puts a drill in a
 * 240px-tall box on a 1900px screen. That is fine for reading key
 * points and writing a note, and useless for seeing footwork, which is
 * the entire reason a coach opened it. So watching gets its own view
 * and the panel keeps the jobs it is good at.
 *
 * DESKTOP ONLY, on purpose. On phones the detail panel already goes
 * full screen (see the .detail-panel rule in dashboard.css), so there
 * is nothing here to fix and a second full-screen layer would just be
 * another thing to dismiss. Below the breakpoint the callers fall back
 * to playing in place.
 *
 * It is a layer over the grid rather than a route: you came from
 * somewhere, and Escape should put you back there with your scroll
 * position and your filters intact.
 */
import React, { useEffect, useRef, useCallback } from "react";
import { X, Check } from "lucide-react";
import MuxPlayer from "../components/LazyMuxPlayer";
import { GOLD } from "../constants/theme";
import { shownKeyPoints } from "./keyPoints";

// Matches the point where the layout has room for a 16:9 video and a
// column of notes beside it. Below this the callers play in place.
export const THEATER_MIN_WIDTH = 900;

export function isTheaterViewport() {
  if (typeof window === "undefined") return false;
  try { return window.matchMedia(`(min-width: ${THEATER_MIN_WIDTH}px)`).matches; }
  catch { return window.innerWidth >= THEATER_MIN_WIDTH; }
}

export function VideoTheater({ item, C, dark, onClose, onWatchedThreshold, watched, onToggleWatched }) {
  const progressRef = useRef({ p50: false, ended: false });
  const closeRef = useRef(null);
  // Where focus was before we took over, so Escape hands it back
  // rather than dumping the user at the top of the document.
  const returnFocusRef = useRef(null);

  const close = useCallback(() => { onClose?.(); }, [onClose]);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
    document.addEventListener("keydown", onKey, true);
    // The grid behind must not scroll under the overlay - a scroll
    // wheel over a video that isn't scrollable should do nothing, not
    // quietly move the page you are coming back to.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") { try { el.focus(); } catch {} }
    };
  }, [close]);

  // A new video in the same theater is a new watch.
  useEffect(() => { progressRef.current = { p50: false, ended: false }; }, [item?.id]);

  if (!item) return null;
  const points = shownKeyPoints(item);
  const hasAside = points.length > 0 || !!item.desc;

  return (
    <div
      className="bam-theater"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      onClick={close}
      style={{
        position: "fixed", inset: 0, zIndex: 8000,
        background: "rgba(6,7,9,0.92)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 32,
      }}>
      {/* The scrim closes; the content does not. */}
      <div onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", gap: 24, alignItems: "flex-start",
          width: "100%", maxWidth: hasAside ? 1480 : 1180, maxHeight: "100%" }}>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            position: "relative", width: "100%", aspectRatio: "16 / 9",
            maxHeight: "78vh", background: "#000", borderRadius: 12, overflow: "hidden",
            border: `1px solid ${C.border}`, boxShadow: "0 30px 90px rgba(0,0,0,0.6)",
          }}>
            <MuxPlayer
              playbackId={item.muxPlaybackId}
              streamType="on-demand"
              autoPlay
              accentColor={GOLD}
              metadata={{ video_title: item.title }}
              onTimeUpdate={(e) => {
                const v = e?.target; if (!v || !v.duration) return;
                const pct = (v.currentTime / v.duration) * 100;
                if (!progressRef.current.p50 && pct >= 50) {
                  progressRef.current.p50 = true;
                  try { onWatchedThreshold?.(item, Math.round(pct)); } catch {}
                }
              }}
              onEnded={() => {
                if (progressRef.current.ended) return;
                progressRef.current.ended = true;
                try { onWatchedThreshold?.(item, 100); } catch {}
              }}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#F2F0E9", minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.title}
            </div>
            {item.duration && (
              <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.45)", flexShrink: 0 }}>
                {item.duration}
              </span>
            )}
            <div style={{ flex: 1 }} />
            {onToggleWatched && (
              <button onClick={() => onToggleWatched(item)} aria-pressed={!!watched}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 12px",
                  borderRadius: 15, cursor: "pointer", flexShrink: 0,
                  border: `1px solid ${watched ? GOLD + "99" : "rgba(255,255,255,0.2)"}`,
                  background: "transparent", color: watched ? GOLD : "rgba(255,255,255,0.7)",
                  fontSize: 10, fontWeight: 800, letterSpacing: .8, textTransform: "uppercase" }}>
                <Check size={11} strokeWidth={3} />{watched ? "Watched" : "Mark watched"}
              </button>
            )}
          </div>
        </div>

        {/* Key points beside the video rather than under it: on a wide
            screen there is dead space to the right, and a coach reading
            a cue while the rep plays is the point of having both. */}
        {hasAside && (
          <aside style={{ width: 300, flexShrink: 0, maxHeight: "82vh", overflow: "auto" }}>
            {points.length > 0 && (
              <>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase",
                  color: GOLD, marginBottom: 12 }}>Key points</div>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px" }}>
                  {points.map((p, i) => (
                    <li key={i} style={{ display: "flex", gap: 9, marginBottom: 11,
                      fontSize: 13, lineHeight: 1.5, color: "rgba(255,255,255,0.82)" }}>
                      <Check size={13} color={GOLD} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {item.desc && (
              <div style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.6)" }}>{item.desc}</div>
            )}
          </aside>
        )}
      </div>

      <button ref={closeRef} onClick={close} aria-label="Close video"
        style={{ position: "fixed", top: 20, right: 24, width: 40, height: 40, borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.16)", background: "rgba(12,13,16,0.8)",
          color: "rgba(255,255,255,0.8)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
        <X size={18} />
      </button>
    </div>
  );
}

export default VideoTheater;
